import React, { useEffect, useState, useContext, useMemo, useCallback } from 'react';
import {
  Drawer, Backdrop, CircularProgress, Toolbar,
  List, ListItem, ListItemButton, ListItemIcon,
  ListItemText, Divider
} from '@mui/material';
import {
  Inbox as InboxIcon,
  Mail as MailIcon,
  Language as LanguageIcon,
  OpenInBrowser as OpenInBrowserIcon,
  Preview as PreviewIcon,
  Web as WebIcon,
  DomainVerification as DomainVerificationIcon,
  SpaceDashboard as SpaceDashboardIcon,
  SmartButton as SmartButtonIcon,
  CallToAction as CallToActionIcon,
  Speed as SpeedIcon,
  PersonAddAltSharp as PersonAddAltSharpIcon,
  ManageAccountsSharp as ManageAccountsSharpIcon,
  
} from '@mui/icons-material';
import { IoIosArrowForward, IoIosArrowBack } from "react-icons/io";
import { Link, useNavigate, useLocation } from 'react-router-dom';
import AuthContext from '../../auth/Auth-context';
import './SideButton.css';
import KeyboardCommandKeyIcon from '@mui/icons-material/KeyboardCommandKey';
const drawerWidth = 270;

const ROUTE_CONFIG = {
  Searcholl: 'Searcholl', 
  yrr_mynewtab: 'YRR MyNewTab', 
  yrr_wundrfly: 'YRR Wundrfly',
  lukkol: 'Lukkol',
  Mynewtab: 'Mynewtab', 
  wundrfly: 'Wundrfly', 
  ollreview: 'OllReview',
  searchhello: 'Searchhello', 
  safesearchengine: 'SafeSearchEngine',
  safesearchengine_net: 'SafeSearchEngine.net', 
  safesearchengine_org: 'SafeSearchEngine.org',
  taskticks: 'Task Ticks', 
  Lukall: 'Lukall', 
  vayutab: 'VayuTab',
  speedytest: 'speedytest', 
  amazeai: 'amazeai',
};

const sidebarItems = Object.keys(ROUTE_CONFIG);

const icons = [
  <InboxIcon />, <MailIcon />, <LanguageIcon />, <MailIcon />, <OpenInBrowserIcon />,
  <PreviewIcon />, <WebIcon />, <DomainVerificationIcon />, <SpaceDashboardIcon />,
  <SmartButtonIcon />, <CallToActionIcon />, <SpeedIcon />, <SmartButtonIcon />,
  <SmartButtonIcon />, <MailIcon />, <WebIcon />, <OpenInBrowserIcon />
];

const SideButton = () => {
  const authctx = useContext(AuthContext);
  const [selectedTab, setSelectedTab] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [accessCheck, setAccessCheck] = useState();
  const navigate = useNavigate();
  const location = useLocation();

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Track route and update selected tab
  useEffect(() => {
    const currentRoute = location.pathname.split('/admin/')[1];
    if (currentRoute) setSelectedTab(currentRoute.toLowerCase());
  }, [location.pathname]);

  // Auth context listener
  useEffect(() => {
    const urlSegment = window.location.pathname.split('/').pop()?.toLowerCase();
    if (urlSegment) setSelectedTab(urlSegment);
    if (authctx.checkAcess) {
      setAccessCheck(authctx.checkAcess.Level);
      setIsDrawerOpen(false);
    }
  }, [authctx]);

  const handleRouteChange = useCallback((route) => {
    setSelectedTab(route.toLowerCase());
    navigate(`/admin/${route}`);
  }, [navigate]);

  const renderSidebarItems = useMemo(() => {
    return sidebarItems.map((route, index) => {
      const isSelected = selectedTab === route.toLowerCase();
      const canAccess = accessCheck === 'Full Access' || authctx?.checkAcess?.SitePermission?.includes(route);

      if (!canAccess) return null;

      return (
        <ListItem key={route} disablePadding>
          <ListItemButton
            onClick={() => handleRouteChange(route)}
            sx={{
              backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
              borderLeft: isSelected ? '4px solid #1976d2' : 'none',
              '&:hover': { backgroundColor: isSelected ? '#e3f2fd' : '#f5f5f5' },
              transition: 'all 0.2s ease',
              pl: isSelected ? 2 : 3,
            }}
          >
            <ListItemIcon sx={{ minWidth: '36px', color: isSelected ? '#1976d2' : 'inherit' }}>
              {icons[index] || <InboxIcon />}
            </ListItemIcon>
            <ListItemText
              primary={ROUTE_CONFIG[route]}
              sx={{ color: isSelected ? '#1976d2' : 'inherit', fontWeight: isSelected ? 600 : 400 }}
            />
          </ListItemButton>
        </ListItem>
      );
    });
  }, [selectedTab, accessCheck, authctx, handleRouteChange]);

  return (
    <>
      {isMobile && (
        <button
          className="slider-button fixed top-4 left-4 z-50 bg-white p-2 rounded-full"
          onClick={() => setIsDrawerOpen(!isDrawerOpen)}
          style={{ boxShadow: '0px 1px 9px 0px rgb(0 0 0 / 14%)' }}
        >
          {isDrawerOpen ? (
            <IoIosArrowBack size={24} color="rgba(128, 128, 128, 0.67)" />
          ) : (
            <IoIosArrowForward size={24} color="rgba(128, 128, 128, 0.67)" />
          )}
        </button>
      )}

      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
          display: { xs: 'block', md: 'block' },
        }}
        variant={isMobile ? "temporary" : "permanent"}
        anchor="left"
        open={isMobile ? isDrawerOpen : true}
        onClose={() => setIsDrawerOpen(false)}
      >
        <Toolbar />
        <Divider />
        <List>{renderSidebarItems}</List>
        {accessCheck === 'Full Access' && (
          <>
            <Divider />
            <List>
              {[
                { label: 'Invite users', icon: <PersonAddAltSharpIcon />, route: 'Invite_Users' },
                { label: 'User Management', icon: <ManageAccountsSharpIcon />, route: 'User_Management' },
                { label: 'Add Keyword', icon: <KeyboardCommandKeyIcon />, route: 'Add_Keyword' }
              ].map(({ label, icon, route }) => (
                <ListItem key={route} disablePadding>
                  <Link to={`/admin/${route}`} style={{ textDecoration: 'none', display: 'block', width: '100%' }}>
                    <ListItemButton>
                      <ListItemIcon sx={{ minWidth: '36px' }}>{icon}</ListItemIcon>
                      <ListItemText primary={label} />
                    </ListItemButton>
                  </Link>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </Drawer>
    </>
  );
};

export default React.memo(SideButton);
